import { useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { useState, type ReactNode } from "react";

import { carryFilters } from "@/lib/filters";
import { cn } from "@/lib/utils";

import { Chip, FlagChip, LinkChip } from "../../chips";
import { NO_FOLDER, NO_FOLDER_TITLE, offersSearch, offersTarget } from "../item_overview/Quotes";
import { readShared } from "../../filters";
import { BackLink, ItemLink, TabChip, TabLink } from "../../links";
import { COPY, TabPage } from "../../PageFrame";
import { useFilterOptions } from "../../SharedFilterBar";
import { MiniBar } from "../bars";
import type { PageProps } from "../index";
import { dateGB, f1, f2, MIN_STANDARD, PageHead } from "../kit";
import { Block, ListTable, NotScoredNote } from "../overview";
import { evidenceSearch } from "../ranking_table/Page";

export interface ScoredCriterion {
  criterion: string;
  question: string | null;
  rating: number;
}

export interface FeatureScore {
  feature: string;
  rating: number;
  criteria_count: number;
  strength: boolean;
}

export interface CriterionLine {
  criterion: string;
  feature: string | null;
  criteria_phase: string | null;
  question: string | null;
  rating: number;
  comment: string | null;
}

export interface OptionOverview {
  option_id: string;
  option_name: string;
  item_id: string;
  item_name: string;
  funnel: string;
  phase: string | null;
  dropped: boolean;
  drop_reason: string | null;
  created_time: string | null;
  phase_changed_at: string | null;
  notion_option_url: string;
  company_notion_id: string | null;
  notion_company_url: string | null;
  /** The option's Drive folder link (C1 rev 2.1); null or absent when not set in Notion. */
  drive_folder?: string | null;
  quote_status: { quoted: boolean; offers_n: number; latest_quote_date: string | null };
  score_profile: { rating: number | null; criteria_count: number; not_scored: boolean; low_evidence: boolean };
  highest: ScoredCriterion | null;
  lowest: ScoredCriterion | null;
  /** Best rated feature first. */
  features: FeatureScore[];
  communication: { feature: string; rating: number } | null;
  criteria: CriterionLine[];
  also_option_for: Array<{ item_id: string; item_name: string }>;
  /** The variants this option has a live offer in (Ioan 2026-10-16); absent from an older API. */
  offer_variants?: string[];
}

/**
 * Where this option's Offers button goes, and with what (Ioan 2026-10-16): an ingredients option opens the
 * Ingredients Funnel on a variant it quoted, every offer shown and its own bar marked; any other option
 * opens the Sourcing Funnel on just this option. Both on the option's item, and never filtered so that the
 * option itself is hidden: a Funnel or Phase it is not in is cleared, and a dropped option shows dropped offers.
 */
export function optionOffers(data: OptionOverview) {
  const variants = data.offer_variants ?? null;
  const tabKey = offersTarget(data.funnel, variants);
  const search = (previous: Record<string, unknown>): Record<string, unknown> => {
    const next: Record<string, unknown> = { ...carryFilters(previous), f_item: data.item_id };
    if (previous.f_item !== data.item_id) {
      delete next.f_variant;
      delete next.f_line;
    }
    if (typeof next.f_funnel === "string" && next.f_funnel !== data.funnel) delete next.f_funnel;
    if (typeof next.f_phase === "string" && next.f_phase !== data.phase) delete next.f_phase;
    const hidden = data.dropped || data.phase === "ARCHIVE";
    if (hidden && next.f_dropped !== "show" && next.f_dropped !== "only") next.f_dropped = "show";
    if (tabKey === "sourcing_funnel") return { ...next, f_option: data.option_id };
    delete next.f_option;
    return { ...offersSearch(next, variants), mark: data.option_id };
  };
  return { tabKey, search };
}

/** "yes (3 offers, latest 12 Aug 2026)" or "no". */
export function quotedText(status: OptionOverview["quote_status"]): string {
  if (!status.quoted) return "no";
  const offers = `${status.offers_n} offer${status.offers_n === 1 ? "" : "s"}`;
  return status.latest_quote_date ? `yes (${offers}, latest ${dateGB(status.latest_quote_date)})` : `yes (${offers})`;
}

export type CommunicationState =
  | { kind: "scored"; feature: string; rating: number }
  | { kind: "unscored"; feature: string }
  | { kind: "absent" };

/**
 * The communication feature: scored on this option, in the item's criteria but not scored here,
 * or not among the item's criteria at all. `itemFeatures` are the features rated on any option of
 * the item; null while that list is not known, and then nothing is claimed about an unscored feature.
 */
export function communicationState(
  communication: OptionOverview["communication"],
  itemFeatures: string[] | null,
): CommunicationState | null {
  if (communication) return { kind: "scored", feature: communication.feature, rating: communication.rating };
  if (itemFeatures === null) return null;
  const feature = itemFeatures.find((name) => /communication/i.test(name));
  return feature ? { kind: "unscored", feature } : { kind: "absent" };
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-[11px] tracking-[0.04em] text-ks-grey-1 uppercase">{label}</dt>
      <dd className="mt-0.5 mb-1.5 text-sm">{children}</dd>
    </>
  );
}

function Extreme({ value, danger }: { value: ScoredCriterion; danger: boolean }) {
  return (
    <>
      <b className={cn(danger && value.rating < MIN_STANDARD && "text-ks-danger")}>{value.rating}</b> {value.criterion}
      {value.question ? <div className="mt-0.5 text-xs text-ks-grey-1">{value.question}</div> : null}
    </>
  );
}

export function ScoreProfile({ data, itemFeatures }: { data: OptionOverview; itemFeatures: string[] | null }) {
  const profile = data.score_profile;
  if (profile.not_scored) {
    return (
      <Block title="Score profile">
        <NotScoredNote />
      </Block>
    );
  }
  const strengths = data.features.filter((feature) => feature.strength);
  const weaknesses = data.features.filter((feature) => !feature.strength);
  const communication = communicationState(data.communication, itemFeatures);
  return (
    <Block title="Score profile">
      {/* Full width (Ioan 2026-10-15): the feature column gets room, and long names wrap onto a second line. */}
      <dl className="grid grid-cols-1 gap-x-8 gap-y-3 lg:grid-cols-[minmax(12rem,1fr)_minmax(0,2fr)]">
        <div>
          <Field label="Average rating">
            <b className="text-xl">{f2(profile.rating)}</b> · {profile.criteria_count} criteria{" "}
            {profile.low_evidence ? <FlagChip kind="low_evidence" /> : null}
          </Field>
        </div>
        <div>
          <Field label="Per feature">
            {data.features.map((feature) => (
              <div
                key={feature.feature}
                className="my-1 grid grid-cols-[minmax(7rem,40%)_minmax(0,1fr)] items-center gap-3 sm:grid-cols-[minmax(10rem,16rem)_minmax(0,1fr)]"
              >
                <span className="break-words text-xs leading-snug">{feature.feature}</span>
                <div className="min-w-0">
                  <MiniBar value={feature.rating} label={feature.feature} />
                </div>
              </div>
            ))}
          </Field>
        </div>
        <div>
          {data.highest ? (
            <Field label="Highest">
              <Extreme value={data.highest} danger={false} />
            </Field>
          ) : null}
          {data.lowest ? (
            <Field label="Lowest">
              <Extreme value={data.lowest} danger />
            </Field>
          ) : null}
        </div>
        <div>
          <Field label="Strengths (features ≥ 7.0)">
            <span className="flex flex-wrap gap-1">
              {strengths.length > 0 ? (
                strengths.map((feature) => (
                  <Chip key={feature.feature}>
                    {feature.feature} {f1(feature.rating)}
                  </Chip>
                ))
              ) : (
                <Chip tone="muted">None yet</Chip>
              )}
            </span>
          </Field>
          <Field label="Weaknesses (below 7.0)">
            <span className="flex flex-wrap gap-1">
              {weaknesses.length > 0 ? (
                weaknesses.map((feature) => (
                  <Chip key={feature.feature} tone="warn">
                    {feature.feature} {f1(feature.rating)}
                  </Chip>
                ))
              ) : (
                <Chip tone="muted">None</Chip>
              )}
            </span>
          </Field>
          {communication ? (
            <Field label="Communication">
              {communication.kind === "scored" ? (
                <span className="rounded-[4px] border-l-[3px] border-ks-purple bg-ks-purple-4 px-2 py-1">
                  <b>{f1(communication.rating)}</b> {communication.feature}
                </span>
              ) : communication.kind === "unscored" ? (
                <Chip tone="muted">{communication.feature}: not scored yet</Chip>
              ) : (
                <Chip tone="muted">Communication: not in this item's criteria</Chip>
              )}
            </Field>
          ) : null}
        </div>
      </dl>
    </Block>
  );
}

export const CRITERIA_COLUMNS: ColumnDef<CriterionLine, unknown>[] = [
  { id: "feature", header: "Feature", meta: { sortable: true }, cell: ({ row }) => row.original.feature },
  { id: "criterion", header: "Criterion", meta: { sortable: true }, cell: ({ row }) => row.original.criterion },
  { id: "question", header: "Question", meta: { sortable: true }, cell: ({ row }) => row.original.question },
  {
    id: "rating",
    header: "Rating",
    meta: { align: "right", sortable: true },
    cell: ({ row }) => (
      <span className={cn(row.original.rating < MIN_STANDARD && "font-bold text-ks-danger")}>{row.original.rating}</span>
    ),
  },
  { id: "comment", header: "Comment", meta: { sortable: true }, cell: ({ row }) => row.original.comment },
];

export type CriteriaSortId = "feature" | "criterion" | "question" | "rating" | "comment";
export type CriteriaSort = { id: CriteriaSortId; direction: "asc" | "desc" } | null;

/** One header click: a new column starts A to Z (low to high), then Z to A, then back to the original order. */
export function nextCriteriaSort(current: CriteriaSort, id: string): CriteriaSort {
  const column = id as CriteriaSortId;
  if (current === null || current.id !== column) return { id: column, direction: "asc" };
  return current.direction === "asc" ? { id: column, direction: "desc" } : null;
}

/** The rows in the chosen order; no sort keeps the API's order. Ties keep their original order. */
export function sortCriteria(rows: CriterionLine[], sort: CriteriaSort): CriterionLine[] {
  if (sort === null) return rows;
  const sign = sort.direction === "asc" ? 1 : -1;
  const key = sort.id;
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const order =
        key === "rating"
          ? a.row.rating - b.row.rating
          : String(a.row[key] ?? "").localeCompare(String(b.row[key] ?? ""), undefined, { sensitivity: "base", numeric: true });
      return order !== 0 ? sign * order : a.index - b.index;
    })
    .map(({ row }) => row);
}

export function CriteriaBlock({ data }: { data: OptionOverview }) {
  const [sort, setSort] = useState<CriteriaSort>(null);
  return (
    <Block
      title="Criteria · question, rating, comment"
      aside={
        <TabLink tabKey="criteria_ranking" search={(previous) => evidenceSearch(previous, data.option_id)}>
          See Criteria Ranking
        </TabLink>
      }
    >
      {data.criteria.length > 0 ? (
        <ListTable<CriterionLine>
          columns={CRITERIA_COLUMNS}
          rows={sortCriteria(data.criteria, sort)}
          caption="Every criterion scored on this option"
          wrap
          sort={sort ?? undefined}
          onSort={(id) => setSort((current) => nextCriteriaSort(current, id))}
        />
      ) : (
        <NotScoredNote full={false} />
      )}
    </Block>
  );
}

/** The option's own heading: it replaces the page name once the option is on screen. */
export function OptionHead({ data }: { data: OptionOverview }) {
  const profile = data.score_profile;
  return (
    <div className="flex flex-col gap-2">
      <div className="text-xs">
        <BackLink fallback="ranking_table" />
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
        <h2 className="text-xl font-bold">{data.option_name}</h2>
        <span>
          <ItemLink itemId={data.item_id} from="option_overview">
            {data.item_name}
          </ItemLink>
        </span>
        {data.phase ? <Chip>{data.phase}</Chip> : null}
        {data.dropped ? <Chip tone="muted">Dropped — {data.drop_reason || "no reason recorded"}</Chip> : null}
        {profile.criteria_count > 0 && profile.low_evidence ? <FlagChip kind="low_evidence" /> : null}
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
        <span>
          <b>Quoted:</b> {quotedText(data.quote_status)}
        </span>
        {data.notion_company_url ? null : (
          <span>
            <b>Company:</b> <Chip tone="muted">No company linked</Chip>
          </span>
        )}
        <span className="flex flex-wrap gap-1">
          <LinkChip href={data.notion_option_url} label="Notion option" missingLabel="Notion option" />
          <LinkChip
            href={data.notion_company_url}
            label="Notion company"
            missingLabel="Notion company"
            missingTitle="No company linked in Notion."
          />
          <LinkChip
            href={data.drive_folder ?? null}
            label="Drive folder"
            missingLabel={NO_FOLDER}
            missingTitle={NO_FOLDER_TITLE}
          />
          <TabChip {...optionOffers(data)}>Offers</TabChip>
        </span>
        <span>
          <b>Also an option for:</b>{" "}
          {data.also_option_for.length > 0
            ? data.also_option_for.map((item, index) => (
                <span key={item.item_id}>
                  {index > 0 ? ", " : null}
                  <ItemLink itemId={item.item_id} from="option_overview">
                    {item.item_name}
                  </ItemLink>
                </span>
              ))
            : "Only this item"}
        </span>
      </div>
    </div>
  );
}

export function OptionOverviewBody({ data, itemFeatures }: { data: OptionOverview; itemFeatures: string[] | null }) {
  return (
    <>
      <ScoreProfile data={data} itemFeatures={itemFeatures} />
      <CriteriaBlock data={data} />
    </>
  );
}

export function OptionOverviewPage({ tab, areaName }: PageProps) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const item = readShared(search).item;
  const options = useFilterOptions(item).data?.data ?? null;
  const itemName = options?.items.find((candidate) => candidate.item_id === item)?.item_name;
  // Null until the item's criteria are known, so the page never says a feature is absent on a guess.
  const itemFeatures = options ? Array.from(new Set(options.criteria.map((criterion) => criterion.feature))) : null;
  return (
    <TabPage<OptionOverview>
      tab={tab}
      areaName={areaName}
      notFound={{ when: (data) => data === null, message: COPY.noOption }}
      head={(data) => (data ? <OptionHead data={data} /> : <PageHead title={tab.label} sub={itemName} />)}
    >
      {/* The criteria list belongs to the URL's item: it says nothing about an option of another item. */}
      {(data) => <OptionOverviewBody data={data} itemFeatures={item === data.item_id ? itemFeatures : null} />}
    </TabPage>
  );
}
